import { Component, useCallback, useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { BarChart3, BriefcaseBusiness, Copy, LogOut, Mail, UsersRound, Bot, Eye, MonitorSmartphone, Inbox, MailOpen, FileText, ClipboardList, BellRing } from "lucide-react";
import useSeo from "../hooks/useSeo";
import { api, clearAdminToken, getAdminToken, setAdminToken } from "../lib/api";
import ColdEmailAgent from "./ColdEmailAgent";

const loginInitial = { email: "", password: "" };

function AdminLogin({ onLogin }) {
  const [form, setForm] = useState(loginInitial);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const onChange = (event) => setForm({ ...form, [event.target.name]: event.target.value });
  const submit = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError("");
    try {
      const response = await api.adminLogin(form);
      setAdminToken(response.data.token);
      onLogin();
    } catch (err) {
      setError("Invalid admin credentials.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grid min-h-screen place-items-center bg-[#0A2540] px-5 py-12" data-testid="admin-login-page">
      <form onSubmit={submit} className="w-full max-w-md border border-white/10 bg-white p-8" data-testid="admin-login-form">
        <p className="text-xs font-bold uppercase tracking-[0.22em] text-[#0066FF]" data-testid="admin-login-eyebrow">Secure dashboard</p>
        <h1 className="mt-4 font-heading text-3xl font-black text-[#0A2540]" data-testid="admin-login-title">TalentBridge Admin</h1>
        <p className="mt-3 text-sm leading-6 text-[#475569]" data-testid="admin-login-help-text">Sign in to access leads, candidates, bookings, pipeline, and outreach tools.</p>
        {error && <p className="mt-5 border border-red-200 bg-red-50 p-3 text-sm font-bold text-red-700" data-testid="admin-login-error-message">{error}</p>}
        <div className="mt-6 grid gap-4">
          <input name="email" type="email" value={form.email} onChange={onChange} className="h-12 border border-[#CBD5E1] px-3" data-testid="admin-email-input" aria-label="Admin email" />
          <input name="password" type="password" value={form.password} onChange={onChange} className="h-12 border border-[#CBD5E1] px-3" data-testid="admin-password-input" aria-label="Admin password" />
          <button type="submit" disabled={loading} className="bg-[#0A2540] px-6 py-4 font-bold text-white hover:bg-[#051424] disabled:opacity-60" data-testid="admin-login-submit-button">
            {loading ? "Signing in..." : "Sign in"}
          </button>
        </div>
      </form>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, testId }) {
  return (
    <div className="border border-[#E2E8F0] bg-white p-5" data-testid={testId}>
      <Icon className="h-6 w-6 text-[#0066FF]" />
      <p className="mt-5 font-heading text-3xl font-black text-[#0A2540]" data-testid={`${testId}-value`}>{value}</p>
      <p className="mt-1 text-xs font-bold uppercase tracking-[0.16em] text-[#64748B]" data-testid={`${testId}-label`}>{label}</p>
    </div>
  );
}

function ClickableStatCard({ icon: Icon, label, value, testId, onClick }) {
  return (
    <button type="button" onClick={onClick} className="border border-[#E2E8F0] bg-white p-5 text-left transition-transform hover:-translate-y-0.5 hover:border-[#0066FF]" data-testid={testId}>
      <Icon className="h-6 w-6 text-[#0066FF]" />
      <p className="mt-5 font-heading text-3xl font-black text-[#0A2540]" data-testid={`${testId}-value`}>{value}</p>
      <p className="mt-1 text-xs font-bold uppercase tracking-[0.16em] text-[#64748B]" data-testid={`${testId}-label`}>{label}</p>
    </button>
  );
}

function PipelineCard({ record, stages, type }) {
  const title = type === "employer" ? record.company_name : record.full_name;
  const subtitle = type === "employer" ? record.contact_name : record.role_interest;
  return (
    <article className="border border-[#E2E8F0] bg-white p-4" data-testid={`pipeline-${type}-${record.id}`}>
      <p className="font-heading text-base font-black text-[#0A2540]" data-testid={`pipeline-${type}-title-${record.id}`}>{title}</p>
      <p className="mt-1 text-sm text-[#475569]" data-testid={`pipeline-${type}-subtitle-${record.id}`}>{subtitle}</p>
      <p className="mt-2 text-xs font-semibold text-[#64748B]" data-testid={`pipeline-${type}-email-${record.id}`}>{record.email}</p>
      <select defaultValue={record.stage} onChange={(event) => api.updateStage(type, record.id, event.target.value)} className="mt-4 w-full border border-[#CBD5E1] bg-white px-2 py-2 text-sm font-semibold" data-testid={`pipeline-${type}-stage-select-${record.id}`} aria-label="Pipeline stage">
        {stages.map((stage) => (
          <option key={stage} value={stage}>{stage}</option>
        ))}
      </select>
    </article>
  );
}

function VisitorsView() {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadVisitors = async () => {
      setLoading(true);
      try {
        const response = await api.visitors();
        setSummary(response.data);
      } finally {
        setLoading(false);
      }
    };
    loadVisitors();
  }, []);

  if (loading || !summary) {
    return <div className="border border-[#E2E8F0] bg-white p-6 font-bold text-[#0A2540]" data-testid="visitors-loading-state">Loading visitor report...</div>;
  }

  return (
    <div className="grid gap-8" data-testid="visitors-report-page">
      <section className="border border-[#E2E8F0] bg-[#0A2540] p-6 text-white" data-testid="visitors-hero-section">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-blue-200" data-testid="visitors-eyebrow">Website visitors</p>
        <h2 className="mt-3 font-heading text-3xl font-black" data-testid="visitors-title">Privacy-friendly traffic log</h2>
        <p className="mt-3 max-w-3xl text-sm leading-7 text-slate-200" data-testid="visitors-description">
          Tracks page visits, device/browser details, referrer, and time. IP addresses are not stored.
        </p>
      </section>

      <section className="grid gap-5 md:grid-cols-2 xl:grid-cols-4" data-testid="visitors-stats-section">
        <StatCard icon={Eye} label="Total visits" value={summary.total_visits} testId="visitors-stat-total" />
        <StatCard icon={BarChart3} label="Today" value={summary.today_visits} testId="visitors-stat-today" />
        <StatCard icon={BarChart3} label="7 days" value={summary.seven_day_visits} testId="visitors-stat-seven-days" />
        <StatCard icon={BarChart3} label="30 days" value={summary.thirty_day_visits} testId="visitors-stat-thirty-days" />
      </section>

      <section className="grid gap-6 xl:grid-cols-[0.8fr_1.2fr]" data-testid="visitors-detail-section">
        <div className="border border-[#E2E8F0] bg-white p-5" data-testid="visitors-top-pages-panel">
          <h3 className="font-heading text-xl font-black text-[#0A2540]" data-testid="visitors-top-pages-title">Top pages</h3>
          <div className="mt-5 grid gap-3">
            {summary.top_pages.length === 0 && <p className="text-sm text-[#64748B]" data-testid="visitors-empty-top-pages">No visits logged yet.</p>}
            {summary.top_pages.map((page) => {
              const key = String(page.page_path || "page").replace(/[^a-zA-Z0-9]/g, "-");
              return (
                <div key={page.page_path} className="flex items-center justify-between border border-[#E2E8F0] p-3" data-testid={`visitors-top-page-${key}`}>
                  <span className="text-sm font-bold text-[#0A2540]" data-testid={`visitors-top-page-path-${key}`}>{page.page_path}</span>
                  <span className="text-sm font-black text-[#0066FF]" data-testid={`visitors-top-page-count-${key}`}>{page.visits}</span>
                </div>
              );
            })}
          </div>
        </div>

        <div className="border border-[#E2E8F0] bg-white p-5" data-testid="visitors-recent-panel">
          <h3 className="font-heading text-xl font-black text-[#0A2540]" data-testid="visitors-recent-title">Recent visits</h3>
          <div className="mt-5 grid max-h-[620px] gap-3 overflow-auto pr-2">
            {summary.recent_visits.length === 0 && <p className="text-sm text-[#64748B]" data-testid="visitors-empty-recent">No visitor records yet.</p>}
            {summary.recent_visits.map((visit) => (
              <article key={visit.id} className="border border-[#E2E8F0] bg-[#FBFBFC] p-4" data-testid={`visitor-log-${visit.id}`}>
                <div className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
                  <div>
                    <p className="font-heading text-base font-black text-[#0A2540]" data-testid={`visitor-log-path-${visit.id}`}>{visit.page_path}</p>
                    <p className="mt-1 text-xs font-semibold text-[#64748B]" data-testid={`visitor-log-time-${visit.id}`}>{new Date(visit.created_at).toLocaleString()}</p>
                  </div>
                  <span className="inline-flex items-center gap-2 border border-[#CBD5E1] bg-white px-3 py-1 text-xs font-bold text-[#0A2540]" data-testid={`visitor-log-device-${visit.id}`}>
                    <MonitorSmartphone className="h-3.5 w-3.5" /> {visit.device_type}
                  </span>
                </div>
                <p className="mt-3 text-sm text-[#475569]" data-testid={`visitor-log-browser-${visit.id}`}>{visit.browser} · {visit.operating_system} · {visit.screen_size || "screen unknown"}</p>
                <p className="mt-2 text-xs text-[#64748B]" data-testid={`visitor-log-referrer-${visit.id}`}>Referrer: {visit.referrer || "Direct / unknown"}</p>
              </article>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

function MessagesInboxView({ onRefreshOverview }) {
  const [messages, setMessages] = useState([]);

  const loadMessages = useCallback(async () => {
    const response = await api.contactMessages();
    setMessages(response.data);
  }, []);

  useEffect(() => {
    loadMessages();
  }, [loadMessages]);

  const unreadCount = messages.filter((message) => message.status === "Unread").length;

  return (
    <div className="grid gap-8" data-testid="messages-inbox-page">
      <section className="border border-[#E2E8F0] bg-[#0A2540] p-6 text-white" data-testid="messages-hero-section">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-blue-200" data-testid="messages-eyebrow">Contact Messages</p>
        <div className="mt-3 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
          <div>
            <h2 className="font-heading text-3xl font-black" data-testid="messages-title">Messages Inbox</h2>
            <p className="mt-3 max-w-3xl text-sm leading-7 text-slate-200" data-testid="messages-description">
              Read contact form submissions from employers, candidates, and website visitors.
            </p>
          </div>
          <div className="border border-white/15 bg-white/10 px-5 py-3" data-testid="messages-unread-count-card">
            <p className="text-xs font-bold uppercase tracking-[0.16em] text-blue-100" data-testid="messages-unread-label">Unread</p>
            <p className="font-heading text-3xl font-black" data-testid="messages-unread-value">{unreadCount}</p>
          </div>
        </div>
      </section>

      <section className="grid gap-4" data-testid="messages-list-section">
        {messages.length === 0 && (
          <div className="border border-[#E2E8F0] bg-white p-6 text-sm font-semibold text-[#64748B]" data-testid="messages-empty-state">No contact messages yet.</div>
        )}
        {messages.map((message) => (
          <article key={message.id} className="border border-[#E2E8F0] bg-white p-5" data-testid={`message-card-${message.id}`}>
            <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-3">
                  <span className={`inline-flex items-center gap-2 px-3 py-1 text-xs font-bold ${message.status === "Unread" ? "bg-blue-50 text-[#0066FF]" : message.status === "Archived" ? "bg-slate-100 text-slate-600" : "bg-emerald-50 text-emerald-700"}`} data-testid={`message-status-${message.id}`}>
                    {message.status === "Unread" ? <Inbox className="h-3.5 w-3.5" /> : <MailOpen className="h-3.5 w-3.5" />} {message.status}
                  </span>
                  <span className="text-xs font-semibold text-[#64748B]" data-testid={`message-date-${message.id}`}>{new Date(message.created_at).toLocaleString()}</span>
                </div>
                <h3 className="mt-4 font-heading text-xl font-black text-[#0A2540]" data-testid={`message-subject-${message.id}`}>{message.subject}</h3>
                <p className="mt-2 text-sm font-bold text-[#334155]" data-testid={`message-sender-${message.id}`}>{message.name} · {message.email}</p>
              </div>
              <select defaultValue={message.status} onChange={(event) => api.updateContactMessageStatus(message.id, event.target.value).then(() => onRefreshOverview?.()).catch(() => loadMessages())} className="h-11 border border-[#CBD5E1] bg-white px-3 text-sm font-bold text-[#0A2540]" data-testid={`message-status-select-${message.id}`} aria-label="Message status">
                <option value="Unread">Unread</option>
                <option value="Read">Read</option>
                <option value="Archived">Archived</option>
              </select>
            </div>
            <p className="mt-5 whitespace-pre-line border-l-4 border-[#0066FF] bg-[#F8FAFC] p-4 text-sm leading-7 text-[#475569]" data-testid={`message-body-${message.id}`}>{message.message}</p>
            <div className="mt-4 flex flex-wrap gap-3">
              <a href={`mailto:${message.email}?subject=Re: ${encodeURIComponent(message.subject)}`} className="inline-flex items-center gap-2 bg-[#0A2540] px-4 py-2 text-sm font-bold text-white hover:bg-[#051424]" data-testid={`message-reply-link-${message.id}`}>
                <Mail className="h-4 w-4" /> Reply by email
              </a>
              <button type="button" onClick={() => api.updateContactMessageStatus(message.id, "Read").then(() => onRefreshOverview?.()).catch(() => loadMessages())} className="border border-[#CBD5E1] px-4 py-2 text-sm font-bold text-[#0A2540] hover:border-[#0066FF] hover:text-[#0066FF]" data-testid={`message-mark-read-button-${message.id}`}>
                Mark read
              </button>
            </div>
          </article>
        ))}
      </section>
    </div>
  );
}

class ResumeUsageView extends Component {
  state = { summary: null };

  componentDidMount() {
    api.resumeUsage().then((response) => this.setState({ summary: response.data }));
  }

  render() {
    const { summary } = this.state;
    if (!summary) {
      return <div className="border border-[#E2E8F0] bg-white p-6 font-bold text-[#0A2540]" data-testid="resume-usage-loading-state">Loading resume checker stats...</div>;
    }

    return (
      <div className="grid gap-8" data-testid="resume-usage-page">
      <section className="border border-[#E2E8F0] bg-[#0A2540] p-6 text-white" data-testid="resume-usage-hero-section">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-blue-200" data-testid="resume-usage-eyebrow">Resume Checker</p>
        <h2 className="mt-3 font-heading text-3xl font-black" data-testid="resume-usage-title">Usage Tracking</h2>
        <p className="mt-3 max-w-3xl text-sm leading-7 text-slate-200" data-testid="resume-usage-description">
          See how many people used the free resume checker. Resume text is not stored.
        </p>
      </section>

      <section className="grid gap-5 md:grid-cols-2 xl:grid-cols-5" data-testid="resume-usage-stats-section">
        <StatCard icon={FileText} label="Total checks" value={summary.total_checks} testId="resume-usage-stat-total" />
        <StatCard icon={BarChart3} label="Today" value={summary.today_checks} testId="resume-usage-stat-today" />
        <StatCard icon={BarChart3} label="7 days" value={summary.seven_day_checks} testId="resume-usage-stat-seven-days" />
        <StatCard icon={BarChart3} label="30 days" value={summary.thirty_day_checks} testId="resume-usage-stat-thirty-days" />
        <StatCard icon={BarChart3} label="Avg score" value={summary.average_score} testId="resume-usage-stat-average" />
      </section>

      <section className="grid gap-6 xl:grid-cols-[0.8fr_1.2fr]" data-testid="resume-usage-detail-section">
        <div className="border border-[#E2E8F0] bg-white p-5" data-testid="resume-usage-top-roles-panel">
          <h3 className="font-heading text-xl font-black text-[#0A2540]" data-testid="resume-usage-top-roles-title">Top target roles</h3>
          <div className="mt-5 grid gap-3">
            {summary.top_target_roles.length === 0 && <p className="text-sm text-[#64748B]" data-testid="resume-usage-empty-roles">No target roles entered yet.</p>}
            {summary.top_target_roles.map((role) => {
              const key = String(role.target_role || "role").replace(/[^a-zA-Z0-9]/g, "-");
              return (
                <div key={role.target_role} className="flex items-center justify-between border border-[#E2E8F0] p-3" data-testid={`resume-usage-role-${key}`}>
                  <span className="text-sm font-bold text-[#0A2540]" data-testid={`resume-usage-role-name-${key}`}>{role.target_role}</span>
                  <span className="text-sm font-black text-[#0066FF]" data-testid={`resume-usage-role-count-${key}`}>{role.checks}</span>
                </div>
              );
            })}
          </div>
        </div>

        <div className="border border-[#E2E8F0] bg-white p-5" data-testid="resume-usage-recent-panel">
          <h3 className="font-heading text-xl font-black text-[#0A2540]" data-testid="resume-usage-recent-title">Recent checks</h3>
          <div className="mt-5 grid max-h-[620px] gap-3 overflow-auto pr-2">
            {summary.recent_checks.length === 0 && <p className="text-sm text-[#64748B]" data-testid="resume-usage-empty-recent">No resume checks yet.</p>}
            {summary.recent_checks.map((check) => (
              <article key={check.id} className="border border-[#E2E8F0] bg-[#FBFBFC] p-4" data-testid={`resume-usage-check-${check.id}`}>
                <div className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
                  <div>
                    <p className="font-heading text-base font-black text-[#0A2540]" data-testid={`resume-usage-check-role-${check.id}`}>{check.target_role || "No target role"}</p>
                    <p className="mt-1 text-xs font-semibold text-[#64748B]" data-testid={`resume-usage-check-time-${check.id}`}>{new Date(check.created_at).toLocaleString()}</p>
                  </div>
                  <span className="inline-flex items-center gap-2 border border-[#CBD5E1] bg-white px-3 py-1 text-xs font-bold text-[#0A2540]" data-testid={`resume-usage-check-score-${check.id}`}>
                    Score {check.overall_score} · {check.grade}
                  </span>
                </div>
                <p className="mt-3 text-sm text-[#475569]" data-testid={`resume-usage-check-words-${check.id}`}>Approx. {check.word_count} words checked. Resume text was not stored.</p>
              </article>
            ))}
          </div>
        </div>
      </section>
      </div>
    );
  }
}

class JobPostsView extends Component {
  state = { jobPosts: [] };

  componentDidMount() {
    api.jobPosts().then((response) => this.setState({ jobPosts: response.data }));
  }

  updateStatus(id, status) {
    api.updateJobPostStatus(id, status).then(() => {
      this.setState((state) => ({ jobPosts: state.jobPosts.map((job) => (job.id === id ? { ...job, status } : job)) }));
    });
  }

  render() {
    const { jobPosts } = this.state;
    const newCount = jobPosts.filter((job) => ["New", "Pending"].includes(job.status)).length;
    return (
      <div className="grid gap-8" data-testid="job-posts-page">
        <section className="border border-[#E2E8F0] bg-[#0A2540] p-6 text-white" data-testid="job-posts-hero-section">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-blue-200" data-testid="job-posts-eyebrow">Employer jobs</p>
          <div className="mt-3 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
            <div>
              <h2 className="font-heading text-3xl font-black" data-testid="job-posts-title">Job Posts Inbox</h2>
              <p className="mt-3 max-w-3xl text-sm leading-7 text-slate-200" data-testid="job-posts-description">
                Review company profiles and Calgary job listings before they publish to the public jobs page.
              </p>
            </div>
            <div className="border border-white/15 bg-white/10 px-5 py-3" data-testid="job-posts-new-count-card">
              <p className="text-xs font-bold uppercase tracking-[0.16em] text-blue-100" data-testid="job-posts-new-label">Pending</p>
              <p className="font-heading text-3xl font-black" data-testid="job-posts-new-value">{newCount}</p>
            </div>
          </div>
        </section>

        <section className="grid gap-4" data-testid="job-posts-list-section">
          {jobPosts.length === 0 && <div className="border border-[#E2E8F0] bg-white p-6 text-sm font-semibold text-[#64748B]" data-testid="job-posts-empty-state">No job posts yet.</div>}
          {jobPosts.map((job) => (
            <article key={job.id} className="border border-[#E2E8F0] bg-white p-5" data-testid={`job-post-card-${job.id}`}>
              <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                <div>
                  <div className="flex flex-wrap items-center gap-3">
                    <span className="bg-blue-50 px-3 py-1 text-xs font-bold text-[#0066FF]" data-testid={`job-post-status-${job.id}`}>{job.status === "New" ? "Pending" : job.status}</span>
                    <span className="text-xs font-semibold text-[#64748B]" data-testid={`job-post-date-${job.id}`}>{new Date(job.created_at).toLocaleString()}</span>
                  </div>
                  <h3 className="mt-4 font-heading text-2xl font-black text-[#0A2540]" data-testid={`job-post-title-${job.id}`}>{job.job_title}</h3>
                  <p className="mt-2 text-sm font-bold text-[#334155]" data-testid={`job-post-company-${job.id}`}>{job.company_name} · {job.contact_name} · {job.email}</p>
                </div>
                <select value={job.status === "New" ? "Pending" : job.status} onChange={(event) => this.updateStatus(job.id, event.target.value)} className="h-11 border border-[#CBD5E1] bg-white px-3 text-sm font-bold text-[#0A2540]" data-testid={`job-post-status-select-${job.id}`} aria-label="Job post status">
                  <option value="Pending">Pending</option>
                  <option value="Reviewed">Reviewed</option>
                  <option value="Approved">Approved</option>
                  <option value="Rejected">Rejected</option>
                  <option value="Archived">Archived</option>
                </select>
              </div>
              <div className="mt-5 grid gap-3 md:grid-cols-2 lg:grid-cols-4" data-testid={`job-post-meta-${job.id}`}>
                <p className="bg-[#F8FAFC] p-3 text-sm text-[#475569]" data-testid={`job-post-category-${job.id}`}><strong>Category:</strong> {job.category}</p>
                <p className="bg-[#F8FAFC] p-3 text-sm text-[#475569]" data-testid={`job-post-location-${job.id}`}><strong>Location:</strong> {job.job_location}</p>
                <p className="bg-[#F8FAFC] p-3 text-sm text-[#475569]" data-testid={`job-post-type-${job.id}`}><strong>Type:</strong> {job.job_type}</p>
                <p className="bg-[#F8FAFC] p-3 text-sm text-[#475569]" data-testid={`job-post-pay-${job.id}`}><strong>Pay:</strong> {job.pay_range}</p>
                <p className="bg-[#F8FAFC] p-3 text-sm text-[#475569]" data-testid={`job-post-service-type-${job.id}`}><strong>Path:</strong> {job.service_type || "Free job"}</p>
              </div>
              <div className="mt-5 grid gap-3 md:grid-cols-2" data-testid={`job-post-company-profile-${job.id}`}>
                <p className="bg-[#F8FAFC] p-3 text-sm text-[#475569]" data-testid={`job-post-company-location-${job.id}`}><strong>Company location:</strong> {job.company_location}</p>
                <p className="bg-[#F8FAFC] p-3 text-sm text-[#475569]" data-testid={`job-post-company-size-${job.id}`}><strong>Company size:</strong> {job.company_size || "Not provided"}</p>
                {job.company_description && <p className="bg-[#F8FAFC] p-3 text-sm text-[#475569] md:col-span-2" data-testid={`job-post-company-description-${job.id}`}><strong>Company profile:</strong> {job.company_description}</p>}
              </div>
              <div className="mt-5 grid gap-4 lg:grid-cols-2">
                <div className="border-l-4 border-[#0066FF] bg-[#F8FAFC] p-4" data-testid={`job-post-description-${job.id}`}>
                  <p className="text-xs font-bold uppercase tracking-[0.16em] text-[#64748B]">Description</p>
                  <p className="mt-2 whitespace-pre-line text-sm leading-7 text-[#475569]">{job.job_description}</p>
                </div>
                <div className="border-l-4 border-[#0066FF] bg-[#F8FAFC] p-4" data-testid={`job-post-requirements-${job.id}`}>
                  <p className="text-xs font-bold uppercase tracking-[0.16em] text-[#64748B]">Requirements</p>
                  <p className="mt-2 whitespace-pre-line text-sm leading-7 text-[#475569]">{job.requirements}</p>
                </div>
              </div>
              {(job.benefits || job.notes) && (
                <div className="mt-4 grid gap-4 lg:grid-cols-2">
                  {job.benefits && <p className="text-sm leading-7 text-[#475569]" data-testid={`job-post-benefits-${job.id}`}><strong>Benefits:</strong> {job.benefits}</p>}
                  {job.notes && <p className="text-sm leading-7 text-[#475569]" data-testid={`job-post-notes-${job.id}`}><strong>Notes:</strong> {job.notes}</p>}
                </div>
              )}
              <div className="mt-4 border-l-4 border-emerald-600 bg-emerald-50 p-4" data-testid={`job-post-apply-method-${job.id}`}>
                <p className="text-xs font-bold uppercase tracking-[0.16em] text-emerald-800" data-testid={`job-post-apply-method-label-${job.id}`}>Candidate application method</p>
                <p className="mt-2 text-sm leading-7 text-emerald-900" data-testid={`job-post-apply-method-value-${job.id}`}><strong>{job.apply_method || "Email"}:</strong> {job.apply_method === "External link" ? job.apply_url : job.apply_method === "Instructions" ? job.apply_instructions : (job.apply_email || job.email)}</p>
              </div>
              <div className="mt-4 flex flex-wrap gap-3">
                <a href={`mailto:${job.email}?subject=Re: ${encodeURIComponent(job.job_title)}`} className="inline-flex items-center gap-2 bg-[#0A2540] px-4 py-2 text-sm font-bold text-white hover:bg-[#051424]" data-testid={`job-post-reply-link-${job.id}`}>
                  <Mail className="h-4 w-4" /> Contact employer
                </a>
                <button type="button" onClick={() => this.updateStatus(job.id, "Approved")} className="inline-flex items-center gap-2 bg-emerald-600 px-4 py-2 text-sm font-bold text-white hover:bg-emerald-700" data-testid={`job-post-approve-button-${job.id}`}>Approve & publish</button>
                <button type="button" onClick={() => this.updateStatus(job.id, "Rejected")} className="inline-flex items-center gap-2 border border-red-200 px-4 py-2 text-sm font-bold text-red-700 hover:border-red-500" data-testid={`job-post-reject-button-${job.id}`}>Reject</button>
                <span className="inline-flex items-center px-4 py-2 text-sm font-bold text-[#475569]" data-testid={`job-post-phone-${job.id}`}>{job.phone}</span>
              </div>
            </article>
          ))}
        </section>
      </div>
    );
  }
}

class JobApplicationsView extends Component {
  state = { applications: [] };

  componentDidMount() {
    api.jobApplications().then((response) => this.setState({ applications: response.data }));
  }

  updateStatus(id, status) {
    api.updateJobApplicationStatus(id, status).then(() => {
      this.setState((state) => ({ applications: state.applications.map((item) => (item.id === id ? { ...item, status, sharing_status: status === "Shared with employer" ? "Shared with employer" : item.sharing_status } : item)) }));
    });
  }

  render() {
    const { applications } = this.state;
    const newCount = applications.filter((item) => item.status === "New").length;
    return (
      <div className="grid gap-8" data-testid="job-applications-page">
        <section className="border border-[#E2E8F0] bg-[#0A2540] p-6 text-white" data-testid="job-applications-hero-section">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-blue-200" data-testid="job-applications-eyebrow">TalentBridge applications</p>
          <div className="mt-3 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
            <div>
              <h2 className="font-heading text-3xl font-black" data-testid="job-applications-title">Applications Inbox</h2>
              <p className="mt-3 max-w-3xl text-sm leading-7 text-slate-200" data-testid="job-applications-description">Review candidates for hiring-support jobs before sharing shortlisted people with employers.</p>
            </div>
            <div className="border border-white/15 bg-white/10 px-5 py-3" data-testid="job-applications-new-count-card">
              <p className="text-xs font-bold uppercase tracking-[0.16em] text-blue-100" data-testid="job-applications-new-label">New</p>
              <p className="font-heading text-3xl font-black" data-testid="job-applications-new-value">{newCount}</p>
            </div>
          </div>
        </section>
        <section className="grid gap-4" data-testid="job-applications-list-section">
          {applications.length === 0 && <div className="border border-[#E2E8F0] bg-white p-6 text-sm font-semibold text-[#64748B]" data-testid="job-applications-empty-state">No TalentBridge applications yet.</div>}
          {applications.map((application) => (
            <article key={application.id} className="border border-[#E2E8F0] bg-white p-5" data-testid={`job-application-card-${application.id}`}>
              <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                <div>
                  <div className="flex flex-wrap items-center gap-3">
                    <span className="bg-blue-50 px-3 py-1 text-xs font-bold text-[#0066FF]" data-testid={`job-application-status-${application.id}`}>{application.status}</span>
                    <span className="bg-emerald-50 px-3 py-1 text-xs font-bold text-emerald-700" data-testid={`job-application-sharing-status-${application.id}`}>{application.sharing_status}</span>
                    <span className="text-xs font-semibold text-[#64748B]" data-testid={`job-application-date-${application.id}`}>{new Date(application.created_at).toLocaleString()}</span>
                  </div>
                  <h3 className="mt-4 font-heading text-2xl font-black text-[#0A2540]" data-testid={`job-application-title-${application.id}`}>{application.full_name}</h3>
                  <p className="mt-2 text-sm font-bold text-[#334155]" data-testid={`job-application-job-${application.id}`}>{application.job_title} · {application.company_name}</p>
                  <p className="mt-1 text-sm font-semibold text-[#64748B]" data-testid={`job-application-contact-${application.id}`}>{application.email} · {application.phone}</p>
                </div>
                <select value={application.status} onChange={(event) => this.updateStatus(application.id, event.target.value)} className="h-11 border border-[#CBD5E1] bg-white px-3 text-sm font-bold text-[#0A2540]" data-testid={`job-application-status-select-${application.id}`} aria-label="Application status">
                  <option value="New">New</option>
                  <option value="Reviewed">Reviewed</option>
                  <option value="Shortlisted">Shortlisted</option>
                  <option value="Shared with employer">Shared with employer</option>
                  <option value="Rejected">Rejected</option>
                  <option value="Archived">Archived</option>
                </select>
              </div>
              <div className="mt-5 grid gap-4 lg:grid-cols-2">
                <div className="border-l-4 border-[#0066FF] bg-[#F8FAFC] p-4" data-testid={`job-application-message-${application.id}`}>
                  <p className="text-xs font-bold uppercase tracking-[0.16em] text-[#64748B]">Candidate message</p>
                  <p className="mt-2 whitespace-pre-line text-sm leading-7 text-[#475569]">{application.message}</p>
                </div>
                <div className="border-l-4 border-[#0066FF] bg-[#F8FAFC] p-4" data-testid={`job-application-resume-${application.id}`}>
                  <p className="text-xs font-bold uppercase tracking-[0.16em] text-[#64748B]">Resume preview {application.resume_filename ? `· ${application.resume_filename}` : ""}</p>
                  <p className="mt-2 max-h-72 overflow-auto whitespace-pre-line text-sm leading-7 text-[#475569]">{application.resume_text}</p>
                </div>
              </div>
              <div className="mt-4 flex flex-wrap gap-3">
                <a href={`mailto:${application.email}?subject=Re: ${encodeURIComponent(application.job_title)}`} className="inline-flex items-center gap-2 bg-[#0A2540] px-4 py-2 text-sm font-bold text-white hover:bg-[#051424]" data-testid={`job-application-reply-link-${application.id}`}><Mail className="h-4 w-4" /> Contact candidate</a>
                <a href={`mailto:${application.employer_email}?subject=Shortlisted candidate for ${encodeURIComponent(application.job_title)}&body=${encodeURIComponent(`${application.full_name}\n${application.email}\n${application.phone}\n\nMessage:\n${application.message}`)}`} className="inline-flex items-center gap-2 bg-emerald-600 px-4 py-2 text-sm font-bold text-white hover:bg-emerald-700" data-testid={`job-application-share-link-${application.id}`}><Mail className="h-4 w-4" /> Share shortlist by email</a>
                <button type="button" onClick={() => this.updateStatus(application.id, "Shortlisted")} className="border border-[#CBD5E1] px-4 py-2 text-sm font-bold text-[#0A2540] hover:border-[#0066FF]" data-testid={`job-application-shortlist-button-${application.id}`}>Shortlist</button>
                <button type="button" onClick={() => this.updateStatus(application.id, "Shared with employer")} className="border border-emerald-200 px-4 py-2 text-sm font-bold text-emerald-700 hover:border-emerald-600" data-testid={`job-application-mark-shared-button-${application.id}`}>Mark shared</button>
              </div>
            </article>
          ))}
        </section>
      </div>
    );
  }
}

class JobAlertsView extends Component {
  state = { summary: null };

  componentDidMount() {
    api.jobAlerts().then((response) => this.setState({ summary: response.data }));
  }

  render() {
    const { summary } = this.state;
    if (!summary) {
      return <div className="border border-[#E2E8F0] bg-white p-6 font-bold text-[#0A2540]" data-testid="job-alerts-loading-state">Loading job alert subscribers...</div>;
    }
    return (
      <div className="grid gap-8" data-testid="job-alerts-admin-page">
        <section className="border border-[#E2E8F0] bg-[#0A2540] p-6 text-white" data-testid="job-alerts-hero-section">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-blue-200" data-testid="job-alerts-eyebrow">Candidate alerts</p>
          <div className="mt-3 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
            <div>
              <h2 className="font-heading text-3xl font-black" data-testid="job-alerts-title">Job Alert Subscribers</h2>
              <p className="mt-3 max-w-3xl text-sm leading-7 text-slate-200" data-testid="job-alerts-description">Track people who subscribed to matching job alert emails.</p>
            </div>
            <div className="border border-white/15 bg-white/10 px-5 py-3" data-testid="job-alerts-active-count-card">
              <p className="text-xs font-bold uppercase tracking-[0.16em] text-blue-100" data-testid="job-alerts-active-label">Active</p>
              <p className="font-heading text-3xl font-black" data-testid="job-alerts-active-value">{summary.active_subscribers}</p>
            </div>
          </div>
        </section>
        <section className="grid gap-4" data-testid="job-alerts-list-section">
          {summary.recent_subscribers.length === 0 && <div className="border border-[#E2E8F0] bg-white p-6 text-sm font-semibold text-[#64748B]" data-testid="job-alerts-empty-state">No job alert subscribers yet.</div>}
          {summary.recent_subscribers.map((subscriber) => (
            <article key={subscriber.id} className="border border-[#E2E8F0] bg-white p-5" data-testid={`job-alert-subscriber-${subscriber.id}`}>
              <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                <div>
                  <p className="font-heading text-xl font-black text-[#0A2540]" data-testid={`job-alert-email-${subscriber.id}`}>{subscriber.email}</p>
                  <p className="mt-2 text-sm font-semibold text-[#475569]" data-testid={`job-alert-preferences-${subscriber.id}`}>{subscriber.category || "All categories"} · {subscriber.location || "Any location"}</p>
                </div>
                <span className="bg-emerald-50 px-3 py-1 text-xs font-bold text-emerald-700" data-testid={`job-alert-status-${subscriber.id}`}>{subscriber.status}</span>
              </div>
              <p className="mt-4 text-xs font-semibold text-[#64748B]" data-testid={`job-alert-created-${subscriber.id}`}>Subscribed: {new Date(subscriber.created_at).toLocaleString()}</p>
              {subscriber.last_alert_sent_at && <p className="mt-1 text-xs font-bold text-[#0066FF]" data-testid={`job-alert-last-sent-${subscriber.id}`}>Last alert sent: {new Date(subscriber.last_alert_sent_at).toLocaleString()}</p>}
            </article>
          ))}
        </section>
      </div>
    );
  }
}

class SavedResumesView extends Component {
  state = { resumes: [], category: "All" };

  componentDidMount() {
    api.savedResumes().then((response) => this.setState({ resumes: response.data }));
  }

  updateStatus(id, status) {
    api.updateSavedResumeStatus(id, status);
  }

  downloadResume(resume, format) {
    const content = format === "doc"
      ? `<!DOCTYPE html><html><head><meta charset="utf-8"><title>${resume.target_role || "Saved Resume"}</title></head><body style="font-family:Arial,sans-serif;line-height:1.5;white-space:pre-wrap;">${resume.tailored_resume.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")}</body></html>`
      : resume.tailored_resume;
    const blob = new Blob([content], { type: format === "doc" ? "application/msword" : "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${resume.job_category || "resume"}-${resume.target_role || "tailored"}.${format}`.replace(/\s+/g, "-").toLowerCase();
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  }

  render() {
    const { resumes, category } = this.state;
    const categories = ["All", ...Array.from(new Set(resumes.map((resume) => resume.job_category || "Other")))];
    const visible = category === "All" ? resumes : resumes.filter((resume) => resume.job_category === category);
    const newCount = resumes.filter((resume) => resume.status === "New").length;

    return (
      <div className="grid gap-8" data-testid="saved-resumes-page">
        <section className="border border-[#E2E8F0] bg-[#0A2540] p-6 text-white" data-testid="saved-resumes-hero-section">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-blue-200" data-testid="saved-resumes-eyebrow">Candidate resumes</p>
          <div className="mt-3 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
            <div>
              <h2 className="font-heading text-3xl font-black" data-testid="saved-resumes-title">Saved Resume Library</h2>
              <p className="mt-3 max-w-3xl text-sm leading-7 text-slate-200" data-testid="saved-resumes-description">View resumes users agreed to save for TalentBridge job matching. Organized by job category.</p>
            </div>
            <div className="border border-white/15 bg-white/10 px-5 py-3" data-testid="saved-resumes-new-count-card">
              <p className="text-xs font-bold uppercase tracking-[0.16em] text-blue-100" data-testid="saved-resumes-new-label">New</p>
              <p className="font-heading text-3xl font-black" data-testid="saved-resumes-new-value">{newCount}</p>
            </div>
          </div>
        </section>

        <div className="border border-[#E2E8F0] bg-white p-5" data-testid="saved-resumes-filter-panel">
          <label className="grid max-w-md gap-2" data-testid="saved-resumes-category-field">
            <span className="text-xs font-bold uppercase tracking-[0.16em] text-[#64748B]" data-testid="saved-resumes-category-label">Filter by category</span>
            <select value={category} onChange={(event) => this.setState({ category: event.target.value })} className="h-11 border border-[#CBD5E1] bg-white px-3 text-sm font-bold text-[#0A2540]" data-testid="saved-resumes-category-select">
              {categories.map((item) => <option key={item} value={item}>{item}</option>)}
            </select>
          </label>
        </div>

        <section className="grid gap-4" data-testid="saved-resumes-list-section">
          {visible.length === 0 && <div className="border border-[#E2E8F0] bg-white p-6 text-sm font-semibold text-[#64748B]" data-testid="saved-resumes-empty-state">No saved resumes in this category yet.</div>}
          {visible.map((resume) => (
            <article key={resume.id} className="border border-[#E2E8F0] bg-white p-5" data-testid={`saved-resume-card-${resume.id}`}>
              <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                <div>
                  <div className="flex flex-wrap items-center gap-3">
                    <span className="bg-blue-50 px-3 py-1 text-xs font-bold text-[#0066FF]" data-testid={`saved-resume-category-${resume.id}`}>{resume.job_category}</span>
                    <span className="bg-slate-100 px-3 py-1 text-xs font-bold text-slate-700" data-testid={`saved-resume-status-${resume.id}`}>{resume.status}</span>
                    <span className="text-xs font-semibold text-[#64748B]" data-testid={`saved-resume-date-${resume.id}`}>{new Date(resume.created_at).toLocaleString()}</span>
                  </div>
                  <h3 className="mt-4 font-heading text-2xl font-black text-[#0A2540]" data-testid={`saved-resume-title-${resume.id}`}>{resume.target_role || "Untitled target role"}</h3>
                  <p className="mt-2 text-sm font-bold text-[#334155]" data-testid={`saved-resume-score-${resume.id}`}>Match score: {resume.match_score}</p>
                </div>
                <select defaultValue={resume.status} onChange={(event) => this.updateStatus(resume.id, event.target.value)} className="h-11 border border-[#CBD5E1] bg-white px-3 text-sm font-bold text-[#0A2540]" data-testid={`saved-resume-status-select-${resume.id}`} aria-label="Saved resume status">
                  <option value="New">New</option>
                  <option value="Reviewed">Reviewed</option>
                  <option value="Shortlisted">Shortlisted</option>
                  <option value="Archived">Archived</option>
                </select>
              </div>
              <div className="mt-5 flex flex-wrap gap-2" data-testid={`saved-resume-keywords-${resume.id}`}>
                {resume.ats_keywords.slice(0, 10).map((keyword, index) => <span key={`${keyword}-${index}`} className="bg-[#F8FAFC] px-3 py-1 text-xs font-bold text-[#475569]" data-testid={`saved-resume-keyword-${resume.id}-${index}`}>{keyword}</span>)}
              </div>
              <details className="mt-5 border border-[#E2E8F0] bg-[#FBFBFC] p-4" data-testid={`saved-resume-details-${resume.id}`}>
                <summary className="cursor-pointer font-bold text-[#0A2540]" data-testid={`saved-resume-summary-${resume.id}`}>Preview tailored resume</summary>
                <pre className="mt-4 whitespace-pre-wrap text-sm leading-7 text-[#334155]" data-testid={`saved-resume-text-${resume.id}`}>{resume.tailored_resume}</pre>
              </details>
              <div className="mt-4 flex flex-wrap gap-3">
                <button type="button" onClick={() => this.downloadResume(resume, "doc")} className="inline-flex items-center gap-2 bg-[#0066FF] px-4 py-2 text-sm font-bold text-white hover:bg-[#0052CC]" data-testid={`saved-resume-download-doc-${resume.id}`}><FileText className="h-4 w-4" /> Download Word</button>
                <button type="button" onClick={() => this.downloadResume(resume, "txt")} className="inline-flex items-center gap-2 border border-[#CBD5E1] px-4 py-2 text-sm font-bold text-[#0A2540] hover:border-[#0066FF]" data-testid={`saved-resume-download-txt-${resume.id}`}><DownloadIcon /> Download TXT</button>
              </div>
            </article>
          ))}
        </section>
      </div>
    );
  }
}

function DownloadIcon() {
  return <FileText className="h-4 w-4" />;
}

export default function Admin() {
  useSeo("Admin Dashboard", "TalentBridge Consulting admin dashboard for CRM pipeline, leads, candidates, bookings, and outreach templates.");
  const [authed, setAuthed] = useState(Boolean(getAdminToken()));
  const [overview, setOverview] = useState(null);
  const [pipeline, setPipeline] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [activeView, setActiveView] = useState("dashboard");

  const loadData = useCallback(async () => {
    try {
      const [overviewResponse, pipelineResponse, templatesResponse] = await Promise.all([api.overview(), api.pipeline(), api.templates()]);
      setOverview(overviewResponse.data);
      setPipeline(pipelineResponse.data);
      setTemplates(templatesResponse.data);
    } catch (err) {
      clearAdminToken();
      setAuthed(false);
    }
  }, []);

  useEffect(() => {
    if (authed) loadData();
  }, [authed, loadData]);

  const logout = () => {
    clearAdminToken();
    setAuthed(false);
  };

  const safeCopy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch (error) {
      console.info("Clipboard copy blocked by browser permissions.");
    }
  };

  if (!authed) return <AdminLogin onLogin={() => setAuthed(true)} />;
  if (!overview) return <div className="grid min-h-screen place-items-center bg-[#FBFBFC] font-bold text-[#0A2540]" data-testid="admin-loading-state">Loading dashboard...</div>;
  if (!overview || !pipeline) return <Navigate to="/admin" replace />;

  return (
    <div className="min-h-screen bg-[#F8FAFC] text-[#0B1120]" data-testid="admin-dashboard-page">
      <aside className="fixed left-0 top-0 hidden h-full w-64 bg-[#0A2540] p-6 text-white lg:block" data-testid="admin-sidebar">
        <p className="font-heading text-2xl font-black" data-testid="admin-sidebar-brand">TalentBridge</p>
        <p className="mt-2 text-xs uppercase tracking-[0.18em] text-slate-300" data-testid="admin-sidebar-subtitle">CRM control room</p>
        <nav className="mt-10 grid gap-3 text-sm font-bold text-slate-200" data-testid="admin-sidebar-nav">
          <button type="button" onClick={() => setActiveView("dashboard")} data-testid="admin-nav-overview-link" className="text-left hover:text-white">Overview</button>
          <button type="button" onClick={() => setActiveView("dashboard")} data-testid="admin-nav-pipeline-link" className="text-left hover:text-white">Pipeline</button>
          <button type="button" onClick={() => setActiveView("messages")} data-testid="admin-nav-messages-link" className="inline-flex items-center gap-2 text-left text-blue-200 hover:text-white"><Inbox className="h-4 w-4" /> Messages inbox</button>
          <button type="button" onClick={() => setActiveView("job-posts")} data-testid="admin-nav-job-posts-link" className="inline-flex items-center gap-2 text-left text-blue-200 hover:text-white"><ClipboardList className="h-4 w-4" /> Job posts</button>
          <button type="button" onClick={() => setActiveView("job-applications")} data-testid="admin-nav-job-applications-link" className="inline-flex items-center gap-2 text-left text-blue-200 hover:text-white"><UsersRound className="h-4 w-4" /> Applications</button>
          <button type="button" onClick={() => setActiveView("job-alerts")} data-testid="admin-nav-job-alerts-link" className="inline-flex items-center gap-2 text-left text-blue-200 hover:text-white"><BellRing className="h-4 w-4" /> Job alerts</button>
          <button type="button" onClick={() => setActiveView("dashboard")} data-testid="admin-nav-templates-link" className="text-left hover:text-white">Email templates</button>
          <button type="button" onClick={() => setActiveView("cold-email")} data-testid="admin-nav-cold-email-link" className="inline-flex items-center gap-2 text-left text-blue-200 hover:text-white"><Bot className="h-4 w-4" /> Cold email agent</button>
          <button type="button" onClick={() => setActiveView("visitors")} data-testid="admin-nav-visitors-link" className="inline-flex items-center gap-2 text-left text-blue-200 hover:text-white"><Eye className="h-4 w-4" /> Website visitors</button>
          <button type="button" onClick={() => setActiveView("resume-usage")} data-testid="admin-nav-resume-usage-link" className="inline-flex items-center gap-2 text-left text-blue-200 hover:text-white"><FileText className="h-4 w-4" /> Resume usage</button>
          <button type="button" onClick={() => setActiveView("saved-resumes")} data-testid="admin-nav-saved-resumes-link" className="inline-flex items-center gap-2 text-left text-blue-200 hover:text-white"><FileText className="h-4 w-4" /> Saved resumes</button>
        </nav>
      </aside>
      <main className="lg:pl-64">
        <div className="sticky top-0 z-20 flex items-center justify-between border-b border-[#E2E8F0] bg-white/90 px-5 py-4 backdrop-blur md:px-8" data-testid="admin-topbar">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#64748B]" data-testid="admin-topbar-eyebrow">Admin dashboard</p>
            <h1 className="font-heading text-2xl font-black text-[#0A2540]" data-testid="admin-topbar-title">{activeView === "cold-email" ? "Cold Email Agent" : activeView === "visitors" ? "Website Visitors" : activeView === "messages" ? "Messages Inbox" : activeView === "job-posts" ? "Job Posts" : activeView === "job-applications" ? "Applications" : activeView === "job-alerts" ? "Job Alerts" : activeView === "resume-usage" ? "Resume Usage" : activeView === "saved-resumes" ? "Saved Resumes" : "Pipeline & Outreach"}</h1>
          </div>
          <div className="flex flex-wrap items-center justify-end gap-2">
            <button onClick={() => setActiveView(activeView === "cold-email" ? "dashboard" : "cold-email")} className="inline-flex items-center gap-2 border border-[#CBD5E1] bg-white px-4 py-2 text-sm font-bold text-[#0A2540] hover:border-[#0066FF]" data-testid="admin-toggle-cold-email-button">
              <Bot className="h-4 w-4" /> {activeView === "cold-email" ? "Dashboard" : "Cold email"}
            </button>
            <button onClick={() => setActiveView(activeView === "visitors" ? "dashboard" : "visitors")} className="inline-flex items-center gap-2 border border-[#CBD5E1] bg-white px-4 py-2 text-sm font-bold text-[#0A2540] hover:border-[#0066FF]" data-testid="admin-toggle-visitors-button">
              <Eye className="h-4 w-4" /> {activeView === "visitors" ? "Dashboard" : "Visitors"}
            </button>
            <button onClick={() => setActiveView(activeView === "messages" ? "dashboard" : "messages")} className="inline-flex items-center gap-2 border border-[#CBD5E1] bg-white px-4 py-2 text-sm font-bold text-[#0A2540] hover:border-[#0066FF]" data-testid="admin-toggle-messages-button">
              <Inbox className="h-4 w-4" /> {activeView === "messages" ? "Dashboard" : "Messages"}
            </button>
            <button onClick={() => setActiveView(activeView === "job-posts" ? "dashboard" : "job-posts")} className="inline-flex items-center gap-2 border border-[#CBD5E1] bg-white px-4 py-2 text-sm font-bold text-[#0A2540] hover:border-[#0066FF]" data-testid="admin-toggle-job-posts-button">
              <ClipboardList className="h-4 w-4" /> {activeView === "job-posts" ? "Dashboard" : "Jobs"}
            </button>
            <button onClick={() => setActiveView(activeView === "job-applications" ? "dashboard" : "job-applications")} className="inline-flex items-center gap-2 border border-[#CBD5E1] bg-white px-4 py-2 text-sm font-bold text-[#0A2540] hover:border-[#0066FF]" data-testid="admin-toggle-job-applications-button">
              <UsersRound className="h-4 w-4" /> {activeView === "job-applications" ? "Dashboard" : "Applications"}
            </button>
            <button onClick={() => setActiveView(activeView === "job-alerts" ? "dashboard" : "job-alerts")} className="inline-flex items-center gap-2 border border-[#CBD5E1] bg-white px-4 py-2 text-sm font-bold text-[#0A2540] hover:border-[#0066FF]" data-testid="admin-toggle-job-alerts-button">
              <BellRing className="h-4 w-4" /> {activeView === "job-alerts" ? "Dashboard" : "Alerts"}
            </button>
            <button onClick={() => setActiveView(activeView === "resume-usage" ? "dashboard" : "resume-usage")} className="inline-flex items-center gap-2 border border-[#CBD5E1] bg-white px-4 py-2 text-sm font-bold text-[#0A2540] hover:border-[#0066FF]" data-testid="admin-toggle-resume-usage-button">
              <FileText className="h-4 w-4" /> {activeView === "resume-usage" ? "Dashboard" : "Resume usage"}
            </button>
            <button onClick={() => setActiveView(activeView === "saved-resumes" ? "dashboard" : "saved-resumes")} className="inline-flex items-center gap-2 border border-[#CBD5E1] bg-white px-4 py-2 text-sm font-bold text-[#0A2540] hover:border-[#0066FF]" data-testid="admin-toggle-saved-resumes-button">
              <FileText className="h-4 w-4" /> {activeView === "saved-resumes" ? "Dashboard" : "Saved resumes"}
            </button>
            <button onClick={logout} className="inline-flex items-center gap-2 border border-[#CBD5E1] bg-white px-4 py-2 text-sm font-bold text-[#0A2540] hover:border-[#0A2540]" data-testid="admin-logout-button">
              <LogOut className="h-4 w-4" /> Logout
            </button>
          </div>
        </div>
        <div className="grid gap-10 px-5 py-8 md:px-8" data-testid="admin-dashboard-content">
          {activeView === "cold-email" ? (
            <ColdEmailAgent />
          ) : activeView === "visitors" ? (
            <VisitorsView />
          ) : activeView === "messages" ? (
            <MessagesInboxView onRefreshOverview={loadData} />
          ) : activeView === "job-posts" ? (
            <JobPostsView />
          ) : activeView === "job-applications" ? (
            <JobApplicationsView />
          ) : activeView === "job-alerts" ? (
            <JobAlertsView />
          ) : activeView === "resume-usage" ? (
            <ResumeUsageView />
          ) : activeView === "saved-resumes" ? (
            <SavedResumesView />
          ) : (
          <>
          <section id="overview" data-testid="admin-overview-section">
            <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-9">
              <StatCard icon={BriefcaseBusiness} label="Employer leads" value={overview.employer_count} testId="admin-stat-employers" />
              <ClickableStatCard icon={ClipboardList} label="Job posts" value={overview.job_post_count} testId="admin-stat-job-posts" onClick={() => setActiveView("job-posts")} />
              <ClickableStatCard icon={UsersRound} label="Applications" value={overview.job_application_count || 0} testId="admin-stat-job-applications" onClick={() => setActiveView("job-applications")} />
              <ClickableStatCard icon={BellRing} label="Job alerts" value={overview.job_alert_count || 0} testId="admin-stat-job-alerts" onClick={() => setActiveView("job-alerts")} />
              <StatCard icon={UsersRound} label="Candidates" value={overview.candidate_count} testId="admin-stat-candidates" />
              <StatCard icon={BarChart3} label="Bookings" value={overview.booking_count} testId="admin-stat-bookings" />
              <ClickableStatCard icon={Mail} label="Messages" value={overview.contact_count} testId="admin-stat-messages" onClick={() => setActiveView("messages")} />
              <StatCard icon={Eye} label="Website visits" value={overview.visitor_count} testId="admin-stat-visitors" />
              <ClickableStatCard icon={FileText} label="Resume checks" value={overview.resume_check_count} testId="admin-stat-resume-checks" onClick={() => setActiveView("resume-usage")} />
            </div>
          </section>

          <section id="pipeline" className="grid gap-6" data-testid="admin-pipeline-section">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#0066FF]" data-testid="admin-pipeline-eyebrow">CRM pipeline</p>
              <h2 className="mt-2 font-heading text-3xl font-black text-[#0A2540]" data-testid="admin-pipeline-title">Employer and candidate records</h2>
            </div>
            <div className="grid gap-6 xl:grid-cols-2">
              <div className="border border-[#E2E8F0] bg-[#FBFBFC] p-5" data-testid="admin-employer-pipeline-column">
                <h3 className="font-heading text-xl font-black text-[#0A2540]" data-testid="admin-employer-pipeline-title">Employer pipeline</h3>
                <div className="mt-5 grid gap-4">
                  {pipeline.employers.length === 0 && <p className="text-sm text-[#475569]" data-testid="admin-empty-employers-message">No employer leads yet.</p>}
                  {pipeline.employers.map((record) => <PipelineCard key={record.id} record={record} stages={pipeline.employer_stages} type="employer" />)}
                </div>
              </div>
              <div className="border border-[#E2E8F0] bg-[#FBFBFC] p-5" data-testid="admin-candidate-pipeline-column">
                <h3 className="font-heading text-xl font-black text-[#0A2540]" data-testid="admin-candidate-pipeline-title">Candidate pipeline</h3>
                <div className="mt-5 grid gap-4">
                  {pipeline.candidates.length === 0 && <p className="text-sm text-[#475569]" data-testid="admin-empty-candidates-message">No candidate profiles yet.</p>}
                  {pipeline.candidates.map((record) => <PipelineCard key={record.id} record={record} stages={pipeline.candidate_stages} type="candidate" />)}
                </div>
              </div>
            </div>
          </section>

          <section id="templates" data-testid="admin-email-templates-section">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#0066FF]" data-testid="admin-templates-eyebrow">Outreach</p>
              <h2 className="mt-2 font-heading text-3xl font-black text-[#0A2540]" data-testid="admin-templates-title">Email templates</h2>
            </div>
            <div className="mt-6 grid gap-5 lg:grid-cols-3">
              {templates.map((template) => (
                <article key={template.id} className="border border-[#E2E8F0] bg-white p-5" data-testid={`email-template-${template.id}`}>
                  <p className="text-xs font-bold uppercase tracking-[0.16em] text-[#0066FF]" data-testid={`email-template-audience-${template.id}`}>{template.audience}</p>
                  <h3 className="mt-3 font-heading text-xl font-black text-[#0A2540]" data-testid={`email-template-title-${template.id}`}>{template.title}</h3>
                  <p className="mt-3 text-sm font-bold text-[#0B1120]" data-testid={`email-template-subject-${template.id}`}>{template.subject}</p>
                  <p className="mt-4 whitespace-pre-line text-sm leading-7 text-[#475569]" data-testid={`email-template-body-${template.id}`}>{template.body}</p>
                  <button type="button" onClick={() => safeCopy(`${template.subject}\n\n${template.body}`)} className="mt-5 inline-flex items-center gap-2 border border-[#CBD5E1] px-4 py-2 text-sm font-bold text-[#0A2540] hover:border-[#0066FF] hover:text-[#0066FF]" data-testid={`email-template-copy-button-${template.id}`}>
                    <Copy className="h-4 w-4" /> Copy
                  </button>
                </article>
              ))}
            </div>
          </section>
          </>
          )}
        </div>
      </main>
    </div>
  );
}